/**
 * Builder-canonical entrance animations — the preset vocabulary, the one state
 * normalizer, and the one stamping engine.
 *
 * PURE and worker-safe: no DOM globals are touched at module scope, no builder
 * imports, no `node:` specifiers. `applyEntranceAttrs` takes an element that the
 * CALLER already parsed, so the same function serves the browser renderer and a
 * Node/Worker export path without either one owning a parser.
 *
 * Three rules this module exists to keep true:
 *
 *   1. **The registry is records, not ids.** Validation, the drawer's options
 *      and the generated CSS all derive from `ENTRANCE_PRESETS`, so a preset
 *      cannot exist in one of the three and be missing from the others.
 *   2. **Stamping is clear-first.** Every call removes the entrance attributes
 *      and classes from the whole subtree before writing new ones, so stamping
 *      twice is byte-identical to stamping once and pre-polluted markup heals
 *      instead of accumulating. Export determinism (`export-lock-tests`
 *      run-twice) depends on this.
 *   3. **Baked visible.** A stamped node always carries `pb-revealed`. No JS, a
 *      script error, or an element above the fold therefore all mean "visible",
 *      never "invisible": the hidden state lives entirely behind the ephemeral
 *      `pb-reveal-armed` class that only the runtime adds.
 *
 * `none` is a sentinel, never a record and never stamped.
 */

/** Site/section preset records. `hiddenTransform` is the pre-reveal transform. */
export const ENTRANCE_PRESETS = Object.freeze([
  Object.freeze({ id: 'fade-up', label: 'Fade up', hiddenTransform: 'translateY(24px)' }),
  Object.freeze({ id: 'fade-in', label: 'Fade in', hiddenTransform: null }),
  Object.freeze({
    id: 'slide-left',
    label: 'Slide from left',
    hiddenTransform: 'translateX(-24px)'
  }),
  Object.freeze({
    id: 'slide-right',
    label: 'Slide from right',
    hiddenTransform: 'translateX(24px)'
  }),
  Object.freeze({ id: 'zoom-in', label: 'Zoom in', hiddenTransform: 'scale(0.94)' })
]);

/** The off sentinel. Valid everywhere a preset id is, and never stamped. */
export const ENTRANCE_NONE = 'none';

export const ENTRANCE_DEFAULTS = Object.freeze({
  template: true,
  preset: ENTRANCE_NONE,
  stagger: true
});

export const ENTRANCE_ATTR = 'data-pb-reveal';
export const ENTRANCE_DELAY_ATTR = 'data-pb-reveal-delay';
export const ENTRANCE_REVEALED_CLASS = 'pb-revealed';
export const ENTRANCE_ARMED_CLASS = 'pb-reveal-armed';

/** Emitted on `<html>` by generated documents only — never by the canvas. */
export const ENTRANCE_TEMPLATE_ATTR = 'data-pb-entrance-template';
export const ENTRANCE_TEMPLATE_OFF = 'off';

/** Suppression marker the canvas document carries at parse time (D4). */
export const ENTRANCE_CANVAS_ATTR = 'data-pb-entrance-canvas';

/** The per-section override field name, shared with SECTION_OVERRIDE_FIELDS. */
export const ENTRANCE_OVERRIDE_FIELD = 'revealPreset';

/**
 * EA-6: the per-ELEMENT fields, at `overrides.elements[eid]`. The preset field
 * deliberately reuses the section field's name — one vocabulary — but its
 * semantics are stricter: a real preset id or ABSENT, never 'none' (an element
 * row is present or absent, not "off"). Both names are excluded from element
 * CSS emission (SKIP_PROPS / NON_CSS_FIELDS): entrance is markup, and the
 * element-CSS emitters append `!important`, which golden G4 pins at zero.
 */
export const ENTRANCE_ELEMENT_FIELD = 'revealPreset';
export const ENTRANCE_ELEMENT_DELAY_FIELD = 'revealDelay';

/**
 * eid grammar, fail-closed (contracts §2). Matches editable-utils' stamped ids;
 * the constant lives here rather than importing editing/ into shared/.
 */
export const ENTRANCE_EID_RE = /^[\w-]+$/;

/** The persisted slot marker (`data-pb-editable` never reaches saved HTML). */
const EID_ATTR = 'data-pb-eid';

/**
 * The closed delay vocabulary. Ordinal 0 omits the attribute entirely, so the
 * first staggered child carries no delay and the CSS enumerates exactly these
 * six values. A computed millisecond count would be an open vocabulary and the
 * stylesheet could not be generated from records.
 */
export const ENTRANCE_DELAY_VALUES = Object.freeze([100, 200, 300, 400, 500, 600]);

/** Replaced elements are never stamped — the LCP `<img>` in particular. */
const REPLACED_TAGS = new Set(['IMG', 'PICTURE', 'SVG', 'VIDEO', 'IFRAME', 'CANVAS']);

const PRESET_IDS = new Set(ENTRANCE_PRESETS.map((preset) => preset.id));

/** @returns {boolean} true for a real preset id — `none` is not one. */
export function isEntrancePreset(value) {
  return typeof value === 'string' && PRESET_IDS.has(value);
}

export function getEntrancePreset(id) {
  return ENTRANCE_PRESETS.find((preset) => preset.id === id) || null;
}

function coerceBoolean(value, fallback) {
  if (value === true || value === false) return value;
  return fallback;
}

function coercePreset(value) {
  if (isEntrancePreset(value)) return value;
  return ENTRANCE_NONE;
}

/**
 * The one override sanitizer. `''` and absent both mean inherit, so an override
 * that is anything else — a number, an object, a responsive `{mobile:…}` shape a
 * spacing field would carry, a preset id this build does not know — collapses to
 * inherit rather than to a guess.
 *
 * @returns {'' | 'none' | string} '' for inherit
 */
export function sanitizeEntranceOverride(value) {
  if (value === ENTRANCE_NONE) return ENTRANCE_NONE;
  if (isEntrancePreset(value)) return value;
  return '';
}

/**
 * Normalize `state.entrance` and every section's `overrides.revealPreset` in
 * place. Invariant, not a ladder step: it runs on every load, every collab
 * snapshot adoption and every incoming patch that touches the field, because a
 * malformed shape from an older client or a peer must be repaired rather than
 * migrated once. Idempotent — run twice ≡ once.
 *
 * @param {object} state
 * @returns {object} the same state object
 */
export function normalizeEntranceState(state) {
  if (!state || typeof state !== 'object') return state;

  const raw = state.entrance;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    state.entrance = { ...ENTRANCE_DEFAULTS };
  } else {
    state.entrance = {
      template: coerceBoolean(raw.template, ENTRANCE_DEFAULTS.template),
      preset: coercePreset(raw.preset),
      stagger: coerceBoolean(raw.stagger, ENTRANCE_DEFAULTS.stagger)
    };
  }

  if (Array.isArray(state.pages)) {
    for (const page of state.pages) {
      if (!Array.isArray(page?.sections)) continue;
      for (const section of page.sections) {
        normalizeSectionEntranceOverride(section);
      }
    }
  }
  const shared = state.sharedSections;
  if (shared && typeof shared === 'object') {
    stripEntranceOverride(shared.navigation);
    stripEntranceOverride(shared.footer);
  }

  return state;
}

/**
 * EA-6: sanitize one element record's entrance fields in place (contracts §2).
 * Preset must be a REAL preset id — 'none', unknown ids, objects delete the
 * field (and a preset-less delay is meaningless, so it goes too). Delay must be
 * a member of the closed vocabulary; 0 and every other value delete it.
 */
export function sanitizeElementEntranceFields(element) {
  if (!element || typeof element !== 'object' || Array.isArray(element)) return element;
  if (ENTRANCE_ELEMENT_FIELD in element && !isEntrancePreset(element[ENTRANCE_ELEMENT_FIELD])) {
    delete element[ENTRANCE_ELEMENT_FIELD];
  }
  if (ENTRANCE_ELEMENT_DELAY_FIELD in element) {
    const delay = element[ENTRANCE_ELEMENT_DELAY_FIELD];
    const valid =
      ENTRANCE_DELAY_VALUES.includes(delay) && isEntrancePreset(element[ENTRANCE_ELEMENT_FIELD]);
    if (!valid) delete element[ENTRANCE_ELEMENT_DELAY_FIELD];
  }
  return element;
}

/** Delete element entrance fields outright (shared sections; responsive buckets). */
function deleteElementEntranceFields(elements) {
  if (!elements || typeof elements !== 'object' || Array.isArray(elements)) return;
  for (const element of Object.values(elements)) {
    if (!element || typeof element !== 'object') continue;
    delete element[ENTRANCE_ELEMENT_FIELD];
    delete element[ENTRANCE_ELEMENT_DELAY_FIELD];
  }
}

/** Sanitize one page section's override in place. */
export function normalizeSectionEntranceOverride(section) {
  if (!section || typeof section !== 'object') return section;
  const overrides = section.overrides;
  if (!overrides || typeof overrides !== 'object' || Array.isArray(overrides)) return section;
  if (ENTRANCE_OVERRIDE_FIELD in overrides) {
    const clean = sanitizeEntranceOverride(overrides[ENTRANCE_OVERRIDE_FIELD]);
    if (clean === '') delete overrides[ENTRANCE_OVERRIDE_FIELD];
    else overrides[ENTRANCE_OVERRIDE_FIELD] = clean;
  }
  // EA-6: element entrance is BASE-bucket only. Records under invalid eids keep
  // their other fields — the entrance fields alone are removed.
  if (overrides.elements && typeof overrides.elements === 'object') {
    for (const [eid, element] of Object.entries(overrides.elements)) {
      if (!ENTRANCE_EID_RE.test(eid)) {
        if (element && typeof element === 'object') {
          delete element[ENTRANCE_ELEMENT_FIELD];
          delete element[ENTRANCE_ELEMENT_DELAY_FIELD];
        }
        continue;
      }
      sanitizeElementEntranceFields(element);
    }
  }
  const responsive = overrides.responsive;
  if (responsive && typeof responsive === 'object') {
    for (const bucket of Object.values(responsive)) {
      deleteElementEntranceFields(bucket?.elements);
    }
  }
  return section;
}

/**
 * Shared sections get a stronger rule: v1 never stamps nav/footer, so the field
 * is meaningless there and must not survive into the content hash if a peer
 * writes one.
 */
export function stripEntranceOverride(section) {
  if (!section || typeof section !== 'object') return section;
  const overrides = section.overrides;
  if (!overrides || typeof overrides !== 'object' || Array.isArray(overrides)) return section;
  delete overrides[ENTRANCE_OVERRIDE_FIELD];
  // EA-6: element entrance fields are equally meaningless on nav/footer.
  deleteElementEntranceFields(overrides.elements);
  const responsive = overrides.responsive;
  if (responsive && typeof responsive === 'object') {
    for (const bucket of Object.values(responsive)) {
      deleteElementEntranceFields(bucket?.elements);
    }
  }
  return section;
}

/**
 * EA-6: the element-mode map for one section — `{eid: {preset, delay|null}}`
 * over VALID entries only (real preset, vocab delay, eid grammar, not hidden
 * in the base bucket), or null when the section has none. A non-null return is
 * what flips the section to element mode (contracts §3).
 */
export function sectionElementEntranceMap(section) {
  const elements = section?.overrides?.elements;
  if (!elements || typeof elements !== 'object' || Array.isArray(elements)) return null;
  // Null-prototype: '__proto__' passes the eid grammar, and assigning it onto
  // a plain object would silently poison the map instead of adding an entry.
  const map = Object.create(null);
  let count = 0;
  for (const [eid, element] of Object.entries(elements)) {
    if (!ENTRANCE_EID_RE.test(eid)) continue;
    // '__proto__' passes the grammar but carries prototype semantics for any
    // downstream plain-object consumer of the map; no stamped id is ever
    // named this, so it is hostile input by definition.
    if (eid === '__proto__') continue;
    if (!element || typeof element !== 'object') continue;
    if (element.hidden === true) continue;
    const preset = element[ENTRANCE_ELEMENT_FIELD];
    if (!isEntrancePreset(preset)) continue;
    const rawDelay = element[ENTRANCE_ELEMENT_DELAY_FIELD];
    map[eid] = {
      preset,
      delay: ENTRANCE_DELAY_VALUES.includes(rawDelay) ? rawDelay : null
    };
    count += 1;
  }
  return count > 0 ? map : null;
}

/** True when `entrance` is exactly the defaults — the omit test for D1b. */
export function isDefaultEntranceState(entrance) {
  if (!entrance || typeof entrance !== 'object' || Array.isArray(entrance)) return false;
  return (
    entrance.template === ENTRANCE_DEFAULTS.template &&
    entrance.preset === ENTRANCE_DEFAULTS.preset &&
    entrance.stagger === ENTRANCE_DEFAULTS.stagger
  );
}

/** Remove entrance values that the portable project format represents by absence. */
export function stripDefaultEntranceState(state) {
  if (!state || typeof state !== 'object') return state;
  if (isDefaultEntranceState(state.entrance)) delete state.entrance;

  for (const page of state.pages || []) {
    for (const section of page?.sections || []) normalizeSectionEntranceOverride(section);
  }
  stripEntranceOverride(state.sharedSections?.navigation);
  stripEntranceOverride(state.sharedSections?.footer);
  return state;
}

function readEntrance(state) {
  const raw = state?.entrance;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return ENTRANCE_DEFAULTS;
  return {
    template: coerceBoolean(raw.template, ENTRANCE_DEFAULTS.template),
    preset: coercePreset(raw.preset),
    stagger: coerceBoolean(raw.stagger, ENTRANCE_DEFAULTS.stagger)
  };
}

/**
 * The effective builder entrance for one section, or null when nothing should
 * be stamped. Fail-closed: any shape this function does not recognize resolves
 * to the site default, and an unknown preset id resolves to off.
 *
 * @returns {{preset: string, stagger: boolean} | null}
 */
export function effectiveEntranceFor(state, section) {
  const entrance = readEntrance(state);
  const override = sanitizeEntranceOverride(section?.overrides?.[ENTRANCE_OVERRIDE_FIELD]);
  const preset = override === '' ? entrance.preset : override;
  if (!isEntrancePreset(preset)) return null;
  return { preset, stagger: entrance.stagger };
}

/**
 * True when any PAGE section of this state would be stamped. Shared sections are
 * excluded on purpose: nav/footer are out of scope for v1, so a stray override on
 * one must not pull the entrance stylesheet into every export.
 */
export function stateUsesEntrance(state) {
  if (!state || typeof state !== 'object') return false;
  if (!Array.isArray(state.pages)) return false;
  for (const page of state.pages) {
    if (!Array.isArray(page?.sections)) continue;
    for (const section of page.sections) {
      if (effectiveEntranceFor(state, section)) return true;
      // EA-6: an element-mode section uses the entrance system even when the
      // site/section preset is none.
      if (sectionElementEntranceMap(section)) return true;
    }
  }
  return false;
}

/** The delay attribute value for a stagger ordinal, or null when omitted. */
export function entranceDelayForOrdinal(ordinal) {
  if (!Number.isFinite(ordinal) || ordinal <= 0) return null;
  const index = Math.min(Math.floor(ordinal), ENTRANCE_DELAY_VALUES.length) - 1;
  return ENTRANCE_DELAY_VALUES[index];
}

function elementChildren(el) {
  const kids = el?.children;
  if (!kids) return [];
  return Array.from(kids);
}

function isReplaced(el) {
  return REPLACED_TAGS.has(String(el?.tagName || '').toUpperCase());
}

/**
 * The stagger container: walk down through single-child wrappers until an
 * element has more than one stampable child. CodeStitch sections are almost
 * always `<section> > .cs-container > (content, group)`, so this lands on the
 * container that actually holds the siblings a stagger should walk.
 *
 * Replaced elements are ignored WHILE WALKING as well as while stamping: hero
 * 2470 hangs a decorative `<img class="cs-floater">` off the section root
 * beside its `.cs-container`, and counting it as a branch would stop the walk
 * one level high and stagger a single node. A replaced element is never a
 * stagger participant, so it is never evidence of a branch either.
 *
 * @returns {Element|null} null when the section has no such branch point
 */
export function findStaggerContainer(rootEl) {
  let node = rootEl;
  let guard = 0;
  while (node && guard < 12) {
    guard += 1;
    const stampable = elementChildren(node).filter((el) => !isReplaced(el));
    if (stampable.length > 1) return node;
    if (stampable.length !== 1) return null;
    node = stampable[0];
  }
  return null;
}

function clearEntranceOn(el) {
  let changed = false;
  if (el.hasAttribute(ENTRANCE_ATTR)) {
    el.removeAttribute(ENTRANCE_ATTR);
    changed = true;
  }
  if (el.hasAttribute(ENTRANCE_DELAY_ATTR)) {
    el.removeAttribute(ENTRANCE_DELAY_ATTR);
    changed = true;
  }
  let classesRemoved = false;
  for (const name of [ENTRANCE_REVEALED_CLASS, ENTRANCE_ARMED_CLASS]) {
    if (el.classList?.contains(name)) {
      el.classList.remove(name);
      classesRemoved = true;
    }
  }
  if (classesRemoved) {
    changed = true;
    // Only when WE emptied it: an element that already carried class="" is not
    // ours to tidy, and rewriting it would move bytes in an unrelated export.
    if (el.getAttribute('class') === '') el.removeAttribute('class');
  }
  return changed;
}

/**
 * The ONE stamping engine, called by the renderer's section build and by
 * `cleanSectionHTML` on the export side.
 *
 * @param {Element} rootEl section root, already parsed by the caller
 * @param {{preset: string, stagger: boolean} | null} entrance null clears only
 * @returns {boolean} whether anything about the subtree changed
 */
export function applyEntranceAttrs(rootEl, entrance) {
  if (!rootEl || typeof rootEl.querySelectorAll !== 'function') return false;

  // Clear-first, over the WHOLE subtree including the root: a stale attribute
  // from a previous preset, a wrong delay, or an armed class the runtime never
  // got to remove must not survive into the next stamp.
  let changed = clearEntranceOn(rootEl);
  for (const el of Array.from(rootEl.querySelectorAll(`[${ENTRANCE_ATTR}]`))) {
    changed = clearEntranceOn(el) || changed;
  }
  for (const el of Array.from(rootEl.querySelectorAll(`[${ENTRANCE_DELAY_ATTR}]`))) {
    changed = clearEntranceOn(el) || changed;
  }
  for (const el of Array.from(
    rootEl.querySelectorAll(`.${ENTRANCE_REVEALED_CLASS}, .${ENTRANCE_ARMED_CLASS}`)
  )) {
    changed = clearEntranceOn(el) || changed;
  }

  // EA-6 element mode (contracts §3/§4): `elements` present supersedes the
  // section preset entirely — stamping both would double-animate any nested
  // pair. Document order; OUTERMOST WINS; replaced elements hoist to
  // closest('picture') || self (the one sanctioned relaxation); one target
  // never stamps twice in a pass.
  const elementMap = entrance?.elements;
  if (elementMap && typeof elementMap === 'object') {
    const entries = [];
    for (const el of Array.from(rootEl.querySelectorAll(`[${EID_ATTR}]`))) {
      const eid = el.getAttribute(EID_ATTR);
      if (!ENTRANCE_EID_RE.test(eid || '')) continue;
      const spec = elementMap[eid];
      if (!spec || !isEntrancePreset(spec.preset)) continue;
      entries.push({ el, spec });
    }
    const outermost = entries.filter(
      ({ el }) =>
        !entries.some((other) => other.el !== el && other.el.contains && other.el.contains(el))
    );
    const stamped = new Set();
    for (const { el, spec } of outermost) {
      let target = el;
      if (isReplaced(el)) {
        target = (typeof el.closest === 'function' && el.closest('picture')) || el;
      }
      if (stamped.has(target)) continue;
      stamped.add(target);
      target.setAttribute(ENTRANCE_ATTR, spec.preset);
      if (spec.delay !== null && ENTRANCE_DELAY_VALUES.includes(spec.delay)) {
        target.setAttribute(ENTRANCE_DELAY_ATTR, String(spec.delay));
      }
      target.classList.add(ENTRANCE_REVEALED_CLASS);
      changed = true;
    }
    return changed;
  }

  const preset = entrance?.preset;
  if (!isEntrancePreset(preset)) return changed;

  let targets = [];
  if (entrance.stagger) {
    const container = findStaggerContainer(rootEl);
    if (container) targets = elementChildren(container).filter((el) => !isReplaced(el));
  }
  if (targets.length === 0) targets = [rootEl];

  targets.forEach((el, index) => {
    el.setAttribute(ENTRANCE_ATTR, preset);
    const delay = targets.length > 1 ? entranceDelayForOrdinal(index) : null;
    if (delay !== null) el.setAttribute(ENTRANCE_DELAY_ATTR, String(delay));
    el.classList.add(ENTRANCE_REVEALED_CLASS);
    changed = true;
  });

  return changed;
}
